/**
 * TimelineConflictModal — la línea de tiempo cambió en el servidor (LMX u
 * otra pestaña) mientras había cambios locales sin guardar.
 *
 * Misma regla que el modal del canvas (E1 · SAVE_CONFLICT_GUARD), con los
 * estilos `save-conflict-*` de studio.css:
 *  · "Recargar del servidor" descarta lo local e hidrata lo del servidor.
 *  · "Mantener lo mío" re-sube lo local pisando la versión del servidor a
 *    conciencia (base_version = la que rechazó).
 */
import { AlertTriangle, Download, Loader2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import useTimelineStore from '../../store/timelineStore';
import { fetchTimeline } from '../../services/timelineSync';
import { describeDiff, diffTimelines } from '../../utils/timelineDiff';

const MAX_LINES = 8;

export default function TimelineConflictModal({ projectId }) {
  const conflict = useTimelineStore((s) => s.syncConflict);
  const [busy, setBusy] = useState(null);
  // Envío AP-9 · qué es distinto entre tu pantalla y lo guardado, en palabras.
  const [diff, setDiff] = useState(null);   // null = cargando · [] = iguales

  useEffect(() => {
    if (!conflict || !projectId) return undefined;
    let alive = true;
    setDiff(null);
    fetchTimeline(projectId)
      .then((remote) => {
        if (!alive) return;
        const local = useTimelineStore.getState().serializeTimeline();
        setDiff(diffTimelines(local, remote?.data || {}));
      })
      .catch(() => { if (alive) setDiff(undefined); });
    return () => { alive = false; };
  }, [conflict, projectId]);

  if (!conflict) return null;

  const run = async (which, fn) => {
    setBusy(which);
    try { await fn(); } finally { setBusy(null); }
  };

  return (
    <div className="save-conflict-overlay" data-testid="tl-sync-conflict-modal">
      <div className="save-conflict-card">
        <div className="save-conflict-head">
          <AlertTriangle size={18} />
          <h3>La línea de tiempo cambió desde otro lugar</h3>
        </div>
        <p>
          LMX u otra pestaña guardó una versión más nueva mientras tenías
          cambios sin guardar.
        </p>
        <div className="save-conflict-diff" data-testid="tl-conflict-diff">
          {diff === null && <span className="save-conflict-hint"><Loader2 size={12} className="spin" /> Comparando…</span>}
          {diff === undefined && <span className="save-conflict-hint">No se pudo comparar con lo guardado.</span>}
          {Array.isArray(diff) && diff.length === 0 && (
            <span className="save-conflict-hint">Tu pantalla y lo guardado son iguales: podés mantener lo tuyo sin perder nada.</span>
          )}
          {Array.isArray(diff) && diff.length > 0 && (
            <>
              <b>Qué es distinto entre tu pantalla y lo guardado:</b>
              <ul>
                {diff.slice(0, MAX_LINES).map((d, i) => (
                  <li key={`${d.label}-${i}`}><b>{d.label}</b> ({d.where}): {describeDiff(d)}</li>
                ))}
                {diff.length > MAX_LINES && <li>y {diff.length - MAX_LINES} más</li>}
              </ul>
              <span className="save-conflict-hint">
                Si no estás usando LMX ni otra pestaña, lo de tu pantalla es lo más nuevo: elegí «Mantener lo mío».
              </span>
            </>
          )}
        </div>
        <div className="save-conflict-actions">
          <button
            className="save-conflict-btn primary"
            onClick={() => run('reload', () =>
              useTimelineStore.getState().resolveTimelineConflictReload(projectId))}
            disabled={!!busy}
            data-testid="tl-conflict-reload"
          >
            {busy === 'reload' ? <Loader2 size={13} className="spin" /> : <Download size={13} />}
            Recargar del servidor
          </button>
          <button
            className="save-conflict-btn"
            onClick={() => run('keep', () =>
              useTimelineStore.getState().resolveTimelineConflictKeepMine(projectId))}
            disabled={!!busy}
            data-testid="tl-conflict-keep"
          >
            {busy === 'keep' ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
            Mantener lo mío
          </button>
        </div>
        <span className="save-conflict-hint">
          Recargar descarta tus cambios. Mantener lo tuyo sobrescribe la
          versión del servidor (versión {conflict.serverVersion ?? '—'}).
        </span>
      </div>
    </div>
  );
}
